import type { ApplyRow } from "../../../api/contactRoleEndDateBackfill.service.js";
import { ROLLBACK_HEADERS } from "./backfillCsv.js";

/**
 * Parses a rollback CSV back into restore rows (SOFTSUPP-506).
 *
 * Strict on purpose: this file drives writes that clear or overwrite contact
 * role end dates, so a malformed line is an error to be shown, never a row to
 * be guessed at. Columns are matched by NAME rather than position — the file
 * normally comes straight out of this tool, but a hand-edited or re-exported
 * copy may reorder them — and every problem is reported with its line number
 * so the operator can fix the file rather than binary-search it.
 *
 * Forgiving only about how the file was produced: pasting out of a spreadsheet
 * yields tabs rather than commas, and Excel's "CSV UTF-8" prepends a byte-order
 * mark that would otherwise make the first header read as "﻿contactRoleId".
 */

export type RollbackRow = {
  /** 1-indexed line in the source file, counting the header. */
  lineNumber: number;
  contactRoleId: string;
  /** Null = the role had no end date before the run: restore clears it,
   * putting the role back to active. */
  value: string | null;
};

export type ParseRollbackResult = {
  rows: RollbackRow[];
  /** Per-line problems. A file with any error must not be applied. */
  errors: string[];
};

/**
 * Mirrors zod v4's `z.uuid()`: version nibble 1-8 and variant nibble 8/9/a/b.
 * Same pattern the office-identity-backfill parser uses — contact role ids
 * are UUIDs, and a mangled id is better caught here than as an API "invalid".
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function detectDelimiter(header: string): "," | "\t" {
  const tabs = header.split("\t").length - 1;
  const commas = header.split(",").length - 1;
  return tabs > commas ? "\t" : ",";
}

/**
 * Splits one line into fields, honouring double quotes. A quoted field may
 * contain the delimiter and `""` unescapes to a literal quote.
 */
function splitFields(line: string, delimiter: "," | "\t"): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (inQuotes) {
      if (character === '"') {
        if (line[index + 1] === '"') {
          current += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += character;
      }
    } else if (character === '"' && current === "") {
      inQuotes = true;
    } else if (character === delimiter) {
      fields.push(current);
      current = "";
    } else {
      current += character;
    }
  }
  fields.push(current);
  return fields;
}

export function parseRollback(text: string): ParseRollbackResult {
  const lines = text
    .replace(/^﻿/, "")
    .split(/\r?\n/)
    .map((line, index) => ({ line, lineNumber: index + 1 }))
    .filter(entry => entry.line.trim() !== "");

  const header = lines.shift();
  if (!header) {
    return {
      rows: [],
      errors: [`The file is empty. Expected a header row: ${ROLLBACK_HEADERS.join(",")}`],
    };
  }

  const delimiter = detectDelimiter(header.line);
  const columnIndex = new Map<string, number>();
  splitFields(header.line, delimiter).forEach((name, index) => {
    // First occurrence wins, so a duplicated header cannot silently remap a column.
    const key = name.trim();
    if (!columnIndex.has(key)) {
      columnIndex.set(key, index);
    }
  });

  const missing = ROLLBACK_HEADERS.filter(name => !columnIndex.has(name));
  if (missing.length > 0) {
    // The predicted mistake: both files start with contactRoleId, and a scan
    // report HAS been pasted in here before. Its signature columns give it
    // away — call it out by name instead of listing missing columns.
    const looksLikeScanReport =
      columnIndex.has("contactRoleId") &&
      !columnIndex.has("value") &&
      ["contactName", "companyName", "editedAfterCutover", "losesLastActiveRole"].some(name =>
        columnIndex.has(name),
      );
    if (looksLikeScanReport) {
      return {
        rows: [],
        errors: [
          "This looks like the scan report. The rollback CSV is offered after " +
            "an apply run and contains columns contactRoleId,value.",
        ],
      };
    }
    return {
      rows: [],
      errors: [
        `The file is missing required column(s): ${missing.join(", ")}. ` +
          `Expected a header containing: ${ROLLBACK_HEADERS.join(",")}`,
      ],
    };
  }

  const column = (name: (typeof ROLLBACK_HEADERS)[number]) => columnIndex.get(name);

  const rows: RollbackRow[] = [];
  const errors: string[] = [];
  const seen = new Set<string>();

  for (const { line, lineNumber } of lines) {
    const fields = splitFields(line, delimiter);
    const contactRoleId = (fields[column("contactRoleId") ?? -1] ?? "").trim();
    // NOT trimmed: the previous value is restored verbatim, and an all-blank
    // cell is caught by the date check below rather than silently emptied.
    const rawValue = fields[column("value") ?? -1] ?? "";

    if (!UUID_PATTERN.test(contactRoleId)) {
      errors.push(`Line ${lineNumber}: contactRoleId '${contactRoleId}' is not a valid UUID`);
      continue;
    }
    // An empty cell is meaningful — the role had no end date, restore clears
    // it — but anything non-empty must be a date the API can actually write.
    const value = rawValue === "" ? null : rawValue;
    if (value !== null && Number.isNaN(new Date(value).getTime())) {
      errors.push(`Line ${lineNumber}: value '${value}' is not a valid date`);
      continue;
    }
    if (seen.has(contactRoleId)) {
      // Two rows for the same contact role cannot both be "the previous value" —
      // whichever the API applied last would win, silently.
      errors.push(`Line ${lineNumber}: duplicate row for contact role ${contactRoleId}`);
      continue;
    }
    seen.add(contactRoleId);
    rows.push({ lineNumber, contactRoleId, value });
  }

  if (rows.length === 0 && errors.length === 0) {
    errors.push("That file has a header but no rows.");
  }

  return { rows, errors };
}

/** Shapes a parsed row for the API's restore mode. Null clears the end date. */
export function toRestoreRow(row: RollbackRow): ApplyRow {
  return { contactRoleId: row.contactRoleId, value: row.value };
}
